import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/over-mij", label: "Over mij" },
];

const aanbodLinks = [
  { to: "/sprankelpraktijk", label: "Sprankelpraktijk" },
  { to: "/little-me-time", label: "Little me time" },
  { to: "/zomerkampen", label: "Zomerkampen" },
  { to: "/inschrijven", label: "Inschrijven zomerkamp" },
];

// Mobile menu items. "Aanbod" is a non-link group that expands an inline
// submenu — its `children` field signals the expandable behavior.
type MobileItem =
  | { type: "link"; to: string; label: string }
  | { type: "group"; label: string; children: { to: string; label: string }[] };

const mobileMenuItems: MobileItem[] = [
  { type: "link", to: "/", label: "Home" },
  { type: "link", to: "/over-mij", label: "Over mij" },
  { type: "group", label: "Aanbod", children: aanbodLinks },
  { type: "link", to: "/sfeerbeelden", label: "Sfeerbeelden" },
  { type: "link", to: "/blog", label: "Blog" },
  { type: "link", to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aanbodOpen, setAanbodOpen] = useState(false);
  // Mobile inline submenu — auto-opens if user is already inside one of
  // the Aanbod child routes so they see the active state in context.
  const [mobileAanbodOpen, setMobileAanbodOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // When the drawer opens, expand the Aanbod group automatically if the
  // user is currently on one of its child pages.
  useEffect(() => {
    if (mobileOpen) {
      setMobileAanbodOpen(aanbodLinks.some((l) => location.pathname.startsWith(l.to)));
    }
  }, [mobileOpen, location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;

    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;

    // Snapshot the FULL inline style attribute on body and html so we can
    // restore them byte-for-byte on close. This is safer than tracking
    // individual properties because:
    //   • A page may already use position:fixed / overflow:hidden inline
    //     (e.g. another modal, a CMS-injected style, a sticky layout).
    //   • Custom properties (--foo) and vendor-prefixed values would be
    //     dropped by a property-by-property snapshot.
    //   • If body had no `style` attribute at all, we want it to have NO
    //     attribute again afterwards (not an empty string).
    // We also capture computed values for the properties we override so we
    // can detect whether the page's stylesheet (not inline) was already
    // applying a fixed position or scroll lock — in which case we must
    // not stomp it on close.
    const prevBodyStyleAttr = body.getAttribute("style");
    const prevHtmlStyleAttr = html.getAttribute("style");
    const prevBodyComputed = (() => {
      const cs = window.getComputedStyle(body);
      return { position: cs.position, overflow: cs.overflow };
    })();
    const prevHtmlComputed = (() => {
      const cs = window.getComputedStyle(html);
      return { overflow: cs.overflow, overscrollBehavior: cs.overscrollBehavior };
    })();

    // If the page's stylesheet already pins the body (another open modal,
    // a fullscreen takeover, etc.), do NOT engage our own fixed-position
    // lock — it would double-lock and the restore could break the host
    // layout. We still apply overflow:hidden defensively, but skip the
    // position/top/left/right/width writes.
    const pageAlreadyLocked =
      prevBodyComputed.position === "fixed" ||
      prevBodyComputed.overflow === "hidden";

    if (!pageAlreadyLocked) {
      // iOS-safe scroll lock: fix the body in place at current scroll position
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.left = "0";
      body.style.right = "0";
      body.style.width = "100%";
    }
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    (html.style as CSSStyleDeclaration).overscrollBehavior = "none";

    // Block document-level touchmove so the page underneath cannot scroll
    // (and iOS rubber-band cannot fire), while still permitting scrolling
    // inside the drawer panel itself. We allow the gesture only when its
    // target is inside the panel AND that panel actually has overflow to
    // scroll — otherwise we preventDefault to keep the body fully locked.
    const handleTouchMove = (e: TouchEvent) => {
      const panel = panelRef.current;
      const target = e.target as Node | null;
      if (!panel || !target || !panel.contains(target)) {
        // Touch on backdrop or outside the drawer → block.
        if (e.cancelable) e.preventDefault();
        return;
      }
      // Find the nearest scrollable ancestor inside the panel.
      let node: HTMLElement | null = target as HTMLElement;
      while (node && node !== panel) {
        const style = window.getComputedStyle(node);
        const canScrollY =
          /(auto|scroll|overlay)/.test(style.overflowY) &&
          node.scrollHeight > node.clientHeight;
        if (canScrollY) return; // let the inner element handle the scroll
        node = node.parentElement;
      }
      // Reached the panel root with no scrollable child → block bounce.
      if (e.cancelable) e.preventDefault();
    };
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      // Restore the FULL inline style attribute exactly as it was before
      // the lock engaged. Setting/removing the attribute as a whole is
      // safer than writing properties one-by-one because:
      //   • It also restores any property we did not explicitly set
      //     (e.g. `transform`, `--custom`, vendor prefixes, !important).
      //   • If the page never had a `style` attribute, we remove ours so
      //     the DOM ends up identical to its pre-open state.
      if (prevBodyStyleAttr === null) {
        body.removeAttribute("style");
      } else {
        body.setAttribute("style", prevBodyStyleAttr);
      }
      if (prevHtmlStyleAttr === null) {
        html.removeAttribute("style");
      } else {
        html.setAttribute("style", prevHtmlStyleAttr);
      }
      // Restore scroll position only if WE actually moved the page
      // (i.e. we engaged the position:fixed lock). If the host page was
      // already locked, leave its scroll handling alone.
      if (!pageAlreadyLocked) {
        window.scrollTo(0, scrollY);
      }
      // Reference computed snapshots so they are part of the closure and
      // future maintainers see the intent: we deliberately don't re-apply
      // them — the attribute restore above already captures everything.
      void prevBodyComputed;
      void prevHtmlComputed;
    };
  }, [mobileOpen]);

  // Global Escape handler — closes the mobile drawer regardless of where
  // focus currently lives (backdrop, document body, an iframe wrapper, etc.).
  // The local onKeyDown on the panel only fires when focus is inside the
  // panel; this effect guarantees Escape always works while the drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setMobileOpen(false);
      }
    };
    // Use capture so we beat any other listeners that might call
    // stopPropagation() (e.g. a focused input inside the drawer).
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [mobileOpen]);

  // Hide background content from assistive tech + sequential focus while the
  // mobile drawer is open. Uses the `inert` attribute (removes from focus +
  // a11y tree) plus `aria-hidden` as a fallback for older AT. The Navbar
  // itself is intentionally NOT in the selector list so the hamburger toggle
  // and the drawer remain reachable.
  useEffect(() => {
    if (!mobileOpen) return;
    if (typeof document === "undefined") return;

    const selector = 'main, footer, [data-hide-when-menu-open]';
    const targets = Array.from(
      document.querySelectorAll<HTMLElement>(selector)
    );

    type Snapshot = { hadInert: boolean; prevAriaHidden: string | null };
    const snapshots = new Map<HTMLElement, Snapshot>();

    targets.forEach((el) => {
      snapshots.set(el, {
        hadInert: el.hasAttribute('inert'),
        prevAriaHidden: el.getAttribute('aria-hidden'),
      });
      // `inert` is widely supported on modern browsers; setAttribute keeps it
      // working even on TS lib targets that don't expose the boolean prop.
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    });

    return () => {
      targets.forEach((el) => {
        const snap = snapshots.get(el);
        if (!snap) return;
        if (!snap.hadInert) el.removeAttribute('inert');
        if (snap.prevAriaHidden === null) {
          el.removeAttribute('aria-hidden');
        } else {
          el.setAttribute('aria-hidden', snap.prevAriaHidden);
        }
      });
    };
  }, [mobileOpen]);

  // Focus trap: keep Tab/Shift+Tab cycling inside the mobile drawer while open,
  // move focus to the first link when it opens, and restore focus to the toggle on close.
  useEffect(() => {
    if (!mobileOpen) return;
    const panel = panelRef.current;
    if (!panel) return;

    const getFocusable = () =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
        )
      ).filter((el) => !el.hasAttribute('inert') && el.offsetParent !== null);

    // Move initial focus to the first focusable element inside the drawer.
    const focusFirst = window.setTimeout(() => {
      const focusables = getFocusable();
      focusables[0]?.focus();
    }, 60);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = getFocusable();
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // If focus has escaped the panel, pull it back in.
      if (!active || !panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusFirst);
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the toggle button when the drawer closes.
      // Mobile Safari/Chrome can silently drop a synchronous .focus() call
      // here because the backdrop tap target is being unmounted in the same
      // frame and the toggle may still be mid-transition. We defer to the
      // next paint and retry once if the browser hasn't honored it yet.
      const restoreFocus = () => {
        const btn = toggleRef.current;
        if (!btn) return;
        try {
          btn.focus({ preventScroll: true });
        } catch {
          btn.focus();
        }
        // iOS Safari fallback: if focus didn't land (e.g. tap-to-close
        // gesture consumed it), try again after the next frame.
        if (document.activeElement !== btn) {
          requestAnimationFrame(() => {
            try {
              btn.focus({ preventScroll: true });
            } catch {
              btn.focus();
            }
          });
        }
      };
      // rAF lets AnimatePresence start its exit and the backdrop blur the
      // tapped element before we move focus, which is what mobile Safari
      // expects after a touch-initiated close.
      requestAnimationFrame(restoreFocus);
    };
  }, [mobileOpen]);

  const showSolid = scrolled || !isHome;

  // Determine if a link is active (exact match for home, startsWith for others)
  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isAanbodActive = aanbodLinks.some((l) => isActive(l.to));

  return (
    <>
      <motion.nav
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 0.68, 0.35, 1] }}
        className="fixed top-0 left-0 right-0 transition-all duration-700 ease-[cubic-bezier(0.22,0.68,0.35,1)]"
        style={{
          zIndex: 9999,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          ...(showSolid ? {
          background: 'hsl(40 33% 98% / 0.88)',
          backdropFilter: 'blur(24px) saturate(1.6)',
          WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
          borderBottom: '1px solid hsl(35 14% 89% / 0.3)',
          boxShadow: '0 1px 24px -6px hsl(25 20% 18% / 0.04), 0 4px 48px -16px hsl(32 35% 52% / 0.03), 0 0 1px hsl(25 20% 18% / 0.02)',
        } : {
          background: 'transparent',
          backdropFilter: 'none',
          borderBottom: '1px solid transparent',
          boxShadow: 'none',
        }),
        }}
        data-nav-root
      >
        <div className="max-w-5xl mx-auto px-5 min-[769px]:px-6 lg:px-8">
          <div
            className="nav-row flex items-center justify-between transition-all duration-700 ease-[cubic-bezier(0.22,0.68,0.35,1)]"
            style={{ height: scrolled ? '60px' : '68px', minHeight: 0 }}
          >
            {/* Logo — slightly larger on mobile so it reads as a brand mark */}
            <Link
              to="/"
              className={`font-heading italic transition-all duration-500 tracking-tight ${
                showSolid
                  ? 'text-foreground text-[1.45rem] sm:text-[1.35rem]'
                  : 'text-white text-[1.5rem] sm:text-[1.4rem]'
              }`}
              style={
                !showSolid
                  ? {
                      // Layered halo for the wordmark on the hero:
                      //  • two tight dark shadows for legibility against the
                      //    warm forest video (no banding, no pixelation),
                      //  • two wide, very low-opacity warm-ivory glows for a
                      //    soft aura that reads as "lit from within" rather
                      //    than a saturated yellow ring.
                      // All values use sub-pixel offsets + large blur radii so
                      // the halo stays smooth at any DPR (retina, 1x, 1.25x).
                      textShadow: [
                        '0 1px 2px hsl(25 25% 8% / 0.35)',
                        '0 2px 10px hsl(25 25% 8% / 0.28)',
                        '0 0 22px hsl(38 28% 92% / 0.18)',
                        '0 0 44px hsl(32 30% 70% / 0.12)',
                      ].join(', '),
                    }
                  : {}
              }
            >
              Sprankelien
            </Link>

            {/* Desktop nav */}
            <div data-nav-desktop className="hidden min-[769px]:flex items-center gap-9">
              {navLinks.map((link) => (
              <Link
                  key={link.to}
                  to={link.to}
                  className={`relative text-[13px] font-body font-medium transition-all duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] group ${
                    showSolid ? 'text-foreground/70 hover:text-foreground' : 'text-white/75 hover:text-white'
                  } ${isActive(link.to) ? (showSolid ? '!text-foreground' : '!text-white') : ''}`}
                >
                  <span className="relative">
                    {link.label}
                  </span>
                  {/* Underline indicator */}
                  <span
                    className={`absolute -bottom-1 left-0 right-0 h-[1.5px] rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] ${
                      isActive(link.to)
                        ? (showSolid ? 'bg-accent/40 scale-x-100' : 'bg-white/40 scale-x-100')
                        : 'scale-x-0 group-hover:scale-x-100 ' + (showSolid ? 'bg-accent/25' : 'bg-white/25')
                    }`}
                    style={{ transformOrigin: 'center' }}
                  />
                </Link>
              ))}

              {/* Aanbod dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setAanbodOpen(true)}
                onMouseLeave={() => setAanbodOpen(false)}
              >
                <button
                  className={`relative flex items-center gap-1.5 text-[13px] font-body font-medium transition-all duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] group ${
                    showSolid ? 'text-foreground/70 hover:text-foreground' : 'text-white/75 hover:text-white'
                  } ${isAanbodActive ? (showSolid ? '!text-foreground' : '!text-white') : ''}`}
                >
                  Aanbod
                  <ChevronDown
                    className={`h-3 w-3 transition-transform duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] ${aanbodOpen ? "rotate-180" : ""}`}
                  />
                  <span
                    className={`absolute -bottom-1 left-0 right-0 h-[1.5px] rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] ${
                      isAanbodActive
                        ? (showSolid ? 'bg-accent/40 scale-x-100' : 'bg-white/40 scale-x-100')
                        : 'scale-x-0 group-hover:scale-x-100 ' + (showSolid ? 'bg-accent/25' : 'bg-white/25')
                    }`}
                    style={{ transformOrigin: 'center' }}
                  />
                </button>
                <AnimatePresence>
                  {aanbodOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, filter: "blur(8px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, y: 6, filter: "blur(8px)" }}
                      transition={{ duration: 0.3, ease: [0.22, 0.68, 0.35, 1] }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-56 rounded-2xl border py-3 px-1.5"
                      style={{
                        background: 'hsl(40 33% 98% / 0.95)',
                        backdropFilter: 'blur(24px) saturate(1.6)',
                        WebkitBackdropFilter: 'blur(24px) saturate(1.6)',
                        borderColor: 'hsl(35 14% 89% / 0.4)',
                        boxShadow: '0 8px 40px -8px hsl(25 20% 18% / 0.08), 0 2px 12px -2px hsl(25 20% 18% / 0.04), 0 0 1px hsl(25 20% 18% / 0.04)',
                      }}
                    >
                      {aanbodLinks.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          className={`block px-4 py-2.5 rounded-xl text-[13px] font-body transition-all duration-400 ease-[cubic-bezier(0.22,0.68,0.35,1)] ${
                            isActive(link.to)
                              ? 'text-foreground bg-secondary/60 font-medium'
                              : 'text-foreground/65 hover:text-foreground hover:bg-secondary/40 hover:translate-x-0.5'
                          }`}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {[
                { to: "/sfeerbeelden", label: "Sfeerbeelden" },
                { to: "/blog", label: "Blog" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative text-[13px] font-body font-medium transition-all duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] group ${
                    showSolid ? 'text-foreground/70 hover:text-foreground' : 'text-white/75 hover:text-white'
                  } ${isActive(link.to) ? (showSolid ? '!text-foreground' : '!text-white') : ''}`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 right-0 h-[1.5px] rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,0.68,0.35,1)] ${
                      isActive(link.to)
                        ? (showSolid ? 'bg-accent/40 scale-x-100' : 'bg-white/40 scale-x-100')
                        : 'scale-x-0 group-hover:scale-x-100 ' + (showSolid ? 'bg-accent/25' : 'bg-white/25')
                    }`}
                    style={{ transformOrigin: 'center' }}
                  />
                </Link>
              ))}

              {/* CTA */}
              <motion.div
                className="rounded-full"
                whileHover={{
                  scale: 1.03,
                  boxShadow: '0 0 20px hsl(25 20% 18% / 0.12), 0 0 40px hsl(32 35% 52% / 0.05)',
                }}
                whileTap={{ scale: 0.98 }}
                transition={{
                  duration: 0.6,
                  ease: [0.22, 0.68, 0.35, 1],
                }}
              >
                <Link
                  to="/contact"
                  className="relative overflow-hidden inline-block bg-primary text-primary-foreground px-6 py-2 rounded-full text-[13px] font-medium font-body transition-all duration-500"
                  style={{ boxShadow: '0 2px 8px hsl(25 20% 18% / 0.08)' }}
                >
                  <span className="absolute inset-0 -translate-x-full animate-[shimmer_5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                  <span className="relative">Contact</span>
                </Link>
              </motion.div>
            </div>

            {/* Mobile right cluster — prominent Contact pill + 44px hamburger */}
            <div data-nav-mobile className="min-[769px]:hidden flex items-center gap-2.5">
              {!mobileOpen && (
                <Link
                  to="/contact"
                  className={`hidden min-[431px]:inline-flex items-center h-11 px-5 rounded-full text-[13.5px] font-semibold font-body tracking-wide transition-all duration-500 ${
                    showSolid
                      ? 'bg-primary text-primary-foreground shadow-[0_4px_14px_-2px_hsl(25_20%_18%_/_0.18),0_2px_4px_-1px_hsl(25_20%_18%_/_0.08)]'
                      : 'bg-white text-primary shadow-[0_4px_14px_-2px_rgba(0,0,0,0.18),0_2px_4px_-1px_rgba(0,0,0,0.08)]'
                  }`}
                >
                  Contact
                </Link>
              )}
            <button
              type="button"
              ref={toggleRef}
              id="mobile-menu-toggle"
              data-mobile-trigger
              className={`relative w-11 h-11 flex items-center justify-center rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                showSolid
                  ? 'text-foreground hover:bg-secondary/60'
                  : 'text-white hover:bg-white/15 bg-white/10 backdrop-blur-md ring-1 ring-white/20'
              }`}
              style={{ zIndex: 10000 }}
              onClick={() => setMobileOpen(!mobileOpen)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && mobileOpen) {
                  e.preventDefault();
                  setMobileOpen(false);
                }
              }}
              aria-label={mobileOpen ? "Sluit menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu-panel"
              aria-haspopup="dialog"
            >
              <span className="sr-only">{mobileOpen ? "Menu is geopend" : "Menu is gesloten"}</span>
              <div className="relative w-5 h-3.5" aria-hidden="true">
                <motion.span
                  className="absolute left-0 right-0 h-[1.5px] bg-current rounded-full"
                  animate={mobileOpen
                    ? { top: "50%", rotate: 45, translateY: "-50%" }
                    : { top: 0, rotate: 0, translateY: "0%" }
                  }
                  transition={{ duration: 0.35, ease: [0.22, 0.68, 0.35, 1] }}
                />
                <motion.span
                  className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[1.5px] bg-current rounded-full"
                  animate={mobileOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
                  transition={{ duration: 0.25, ease: [0.22, 0.68, 0.35, 1] }}
                />
                <motion.span
                  className="absolute left-0 right-0 h-[1.5px] bg-current rounded-full"
                  animate={mobileOpen
                    ? { bottom: "50%", rotate: -45, translateY: "50%" }
                    : { bottom: 0, rotate: 0, translateY: "0%" }
                  }
                  transition={{ duration: 0.35, ease: [0.22, 0.68, 0.35, 1] }}
                />
              </div>
            </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Full-screen mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 0.68, 0.35, 1] }}
            className="fixed left-0 right-0 bottom-0 min-[769px]:hidden"
            id="mobile-menu-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Hoofdnavigatie"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                setMobileOpen(false);
              }
            }}
            // Outside click: close when the user taps the backdrop area
            // (anything that isn't inside the drawer aside). We listen on
            // pointerdown for unified mouse+touch handling on Safari/Chrome,
            // and call preventDefault on the touch path so iOS doesn't
            // synthesize a follow-up click that could land on the toggle
            // button underneath and immediately re-open the drawer.
            // Focus is restored to the toggle by the focus-trap effect cleanup.
            onPointerDown={(e) => {
              if (e.target !== e.currentTarget) return;
              // Blur whatever the backdrop tap focused so iOS Safari is
              // willing to move focus to the toggle on close.
              (document.activeElement as HTMLElement | null)?.blur?.();
              setMobileOpen(false);
            }}
            onTouchEnd={(e) => {
              // Suppress the synthesized mouse/click sequence on iOS after
              // the pointerdown above already handled the close.
              if (e.target === e.currentTarget) e.preventDefault();
            }}
            style={{
              top: 0,
              zIndex: 9990,
              background: 'hsl(25 20% 14% / 0.55)',
              backdropFilter: 'blur(28px) saturate(1.4)',
              WebkitBackdropFilter: 'blur(28px) saturate(1.4)',
            }}
          >
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.45, ease: [0.22, 0.68, 0.35, 1] }}
              className="ml-auto flex h-full w-full max-w-[440px] flex-col bg-background/97 px-6 pb-8 shadow-[0_12px_48px_-20px_hsl(25_20%_18%_/_0.22)] overflow-y-auto"
              style={{
                paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
              }}
            >
              {/* Header row inside drawer — brand + close button */}
              <div className="flex items-center justify-between mb-6 min-h-[44px]">
                <span className="font-heading italic text-foreground text-[1.35rem] tracking-tight">
                  Sprankelien
                </span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Sluit menu"
                  className="w-11 h-11 flex items-center justify-center rounded-full text-foreground/80 hover:text-foreground hover:bg-secondary/60 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <nav className="flex flex-col gap-1.5 w-full" aria-label="Mobiele navigatie">
                {mobileMenuItems.map((item, i) => {
                  const baseDelay = 0.08 + i * 0.04;

                  if (item.type === "group") {
                    const groupActive = item.children.some((c) => isActive(c.to));
                    const expanded = mobileAanbodOpen;
                    const panelId = "mobile-aanbod-submenu";
                    return (
                      <motion.div
                        key="aanbod-group"
                        initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: 12 }}
                        transition={{ duration: 0.45, delay: baseDelay, ease: [0.22, 0.68, 0.35, 1] }}
                        className="w-full"
                      >
                        <button
                          type="button"
                          onClick={() => setMobileAanbodOpen((v) => !v)}
                          aria-expanded={expanded}
                          aria-controls={panelId}
                          className={`mobile-menu-link group relative flex min-h-[64px] w-full items-center justify-between rounded-[1.5rem] pl-6 pr-5 py-4 text-left text-[1.9rem] font-heading italic tracking-tight transition-all duration-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                            groupActive
                              ? "bg-secondary text-foreground"
                              : "text-foreground/70 hover:bg-secondary/70 hover:text-foreground"
                          }`}
                        >
                          <span
                            className={`absolute left-0 top-1/2 -translate-y-1/2 h-8 w-[3px] rounded-full bg-accent transition-all duration-400 ${
                              groupActive ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"
                            }`}
                          />
                          <span className="flex items-baseline gap-3">{item.label}</span>
                          <span className="flex items-center gap-3">
                            <span className={`text-[0.95rem] font-body not-italic uppercase tracking-[0.18em] transition-colors duration-300 ${groupActive ? "text-accent" : "text-accent/60"}`}>
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <ChevronDown
                              className={`h-5 w-5 text-foreground/60 transition-transform duration-400 ease-[cubic-bezier(0.22,0.68,0.35,1)] ${expanded ? "rotate-180" : ""}`}
                              aria-hidden="true"
                            />
                          </span>
                        </button>

                        <AnimatePresence initial={false}>
                          {expanded && (
                            <motion.div
                              id={panelId}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.35, ease: [0.22, 0.68, 0.35, 1] }}
                              className="overflow-hidden"
                            >
                              <div className="pl-8 pr-2 pt-2 pb-1 flex flex-col gap-0.5 border-l border-border/40 ml-6 my-1">
                                {item.children.map((child) => {
                                  const childActive = isActive(child.to);
                                  return (
                                    <Link
                                      key={child.to}
                                      to={child.to}
                                      className={`block min-h-[48px] flex items-center rounded-xl px-4 py-2.5 text-[1.05rem] font-body transition-colors duration-300 ${
                                        childActive
                                          ? "text-foreground bg-secondary/70 font-medium"
                                          : "text-foreground/65 hover:text-foreground hover:bg-secondary/40"
                                      }`}
                                    >
                                      {child.label}
                                    </Link>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  }

                  const active = isActive(item.to);
                  return (
                    <motion.div
                      key={item.to}
                      initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.45, delay: baseDelay, ease: [0.22, 0.68, 0.35, 1] }}
                      className="w-full"
                    >
                      <Link
                        to={item.to}
                        className={`mobile-menu-link group relative flex min-h-[64px] items-center justify-between rounded-[1.5rem] pl-6 pr-5 py-4 text-left text-[1.9rem] font-heading italic tracking-tight transition-all duration-400 ${
                          active
                            ? "bg-secondary text-foreground"
                            : "text-foreground/70 hover:bg-secondary/70 hover:text-foreground"
                        }`}
                      >
                        <span
                          className={`absolute left-0 top-1/2 -translate-y-1/2 h-8 w-[3px] rounded-full bg-accent transition-all duration-400 ${
                            active ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"
                          }`}
                        />
                        <span className="flex items-baseline gap-3">{item.label}</span>
                        <span className={`text-[0.95rem] font-body not-italic uppercase tracking-[0.18em] transition-colors duration-300 ${active ? "text-accent" : "text-accent/60 group-hover:text-accent"}`}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </Link>
                    </motion.div>
                  );
                })}

                <motion.div
                  initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: 12 }}
                  transition={{
                    duration: 0.45,
                    delay: 0.08 + mobileMenuItems.length * 0.04,
                    ease: [0.22, 0.68, 0.35, 1],
                  }}
                  className="w-full pt-6"
                >
                  <Link
                    to="/contact"
                    className="relative overflow-hidden flex min-h-[56px] items-center justify-center rounded-full bg-primary px-7 py-[1.05rem] text-[15px] font-semibold text-center font-body text-primary-foreground"
                    style={{ boxShadow: '0 2px 16px hsl(25 20% 18% / 0.12), 0 0 1px hsl(25 20% 18% / 0.04)' }}
                  >
                    <span className="absolute inset-0 -translate-x-full animate-[shimmer_5s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                    <span className="relative">Neem contact op</span>
                  </Link>
                </motion.div>
              </nav>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="mt-auto pt-10 text-[11px] text-muted-foreground/50 font-body tracking-[0.18em] uppercase"
              >
                Voor je kind thuis
              </motion.p>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
